import { memo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useUpdateGroupMutation, useGetIntakesQuery } from '@/entities/Courses';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomInput } from '@/shared/ui/Fields';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import styles from './EditGroupModal.module.scss';

interface EditGroupModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  courseId: number;
  intakeId: number;
  groupNumber: number;
  onSuccess?: () => void;
}

interface GroupFormData {
  title: string;
  number: number;
}

const groupSchema = yup.object({
  title: yup.string().max(255),
  number: yup.number().required('Group number is required').min(1).integer(),
}) as yup.ObjectSchema<GroupFormData>;

export const EditGroupModal = memo(
  ({ isOpen, onOpenChange, courseId, intakeId, groupNumber, onSuccess }: EditGroupModalProps) => {
    const { toast } = useToast();
    const [updateGroup, { isLoading }] = useUpdateGroupMutation();

    const { data: intakesData } = useGetIntakesQuery(
      {
        courseId,
        page: 1,
        pageSize: 1000,
      },
      {
        skip: !isOpen,
      },
    );

    const group = intakesData?.data?.intakes
      ?.find((intake) => intake.id === intakeId)
      ?.groups?.find((g) => g.number === groupNumber);

    const {
      control,
      handleSubmit,
      formState: { errors },
      reset,
      watch,
    } = useForm<GroupFormData>({
      resolver: yupResolver(groupSchema) as any,
      defaultValues: {
        title: '',
        number: 0,
      },
    });

    useEffect(() => {
      if (isOpen && group) {
        reset({
          title: group.title || '',
          number: group.number || groupNumber,
        });
      }
    }, [isOpen, group, reset, groupNumber]);

    const watchedNumber = watch('number');
    const canSubmit = !!watchedNumber && watchedNumber > 0;

    const onSubmit = async (data: GroupFormData) => {
      try {
        const result = await updateGroup({
          intakeId,
          groupNumber,
          body: {
            title: data.title,
            number: data.number,
          },
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Group updated successfully',
          color: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to update group',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      reset({
        title: '',
        number: 0,
      });
      onOpenChange(false);
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        size="2xl"
        classNames={{
          base: 'p-6 flex flex-col gap-5 max-w-[600px]',
          closeButton: styles.closeBtn,
        }}
      >
        <ModalContent>
          {() => (
            <form onSubmit={handleSubmit(onSubmit as any)}>
              <ModalHeader className={styles.title}>Edit group</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-6">
                <Controller
                  name="number"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      value={field.value?.toString() || ''}
                      onChange={(e) => field.onChange(Number(e.target.value) || 0)}
                      type="number"
                      label="Group Number"
                      labelPlacement="outside"
                      placeholder="Enter the group number"
                      errorMessage={errors.number?.message}
                      showError={!!errors.number}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      label="Group Title"
                      labelPlacement="outside"
                      placeholder="Advanced Group"
                      errorMessage={errors.title?.message}
                      showError={!!errors.title}
                    />
                  )}
                />
              </ModalBody>
              <ModalFooter className="gap-4 pt-6 px-0">
                <AppButton fullWidth color="white" onPress={handleClose} type="button">
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  type="submit"
                  isLoading={isLoading}
                  disabled={!canSubmit}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit as any)();
                  }}
                >
                  Save changes
                </AppButton>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

EditGroupModal.displayName = 'EditGroupModal';
